import { oc } from "@orpc/contract";
import { z } from "zod";
import type { SystemEntity } from "@erp/shared-types";
import { UserBasicInfoSchema } from "./core/users.contract";

// --- Enums ---

/**
 * Record types that accept comments ("notes" in the UI). Values are
 * `SystemEntity` keys, so any surface that already identifies a record as
 * `{ entityType, entityId }` can hang a notes thread off it.
 *
 * Adding a type here is not enough on its own: the backend
 * `commentableRegistry` must also say which RBAC subject gates it and how to
 * look the record up inside an organization. See
 * `backend/src/services/comments/README.md`.
 */
export const COMMENTABLE_ENTITY_TYPES = [
  "TASK",
  "AP_INVOICE",
  "IM_DRAFT_PURCHASE_ORDER",
] as const satisfies readonly `${SystemEntity}`[];

/**
 * Exported as a constant beside the schema so callers name it rather than
 * repeating the literal — including the surfaces whose front door is NOT the
 * task (a pricelist update case comments against its companion task), which
 * must not reach into the Sequelize model for the type.
 */
export const TASK_COMMENTABLE_TYPE = "TASK";

/**
 * Feedback reports carry comments too, but only through their own
 * `core.feedback.listComments/addComment` procedures: a report's scope (staff
 * read across organizations) is not a plain `Read` on one subject, so it is not
 * one of the `COMMENTABLE_ENTITY_TYPES` the generic procedures accept.
 */
export const FEEDBACK_COMMENTABLE_TYPE = "FEEDBACK";

/**
 * Item Master review cell comments: each message of a change-set comment
 * thread is a comment on the thread (`im_change_set_comment_threads`). Like
 * feedback, they are reached only through their own
 * `inventoryManagement.changeSetComments.*` procedures, which authorize the
 * review first.
 */
export const CHANGE_SET_COMMENT_THREAD_COMMENTABLE_TYPE =
  "IM_CHANGE_SET_COMMENT_THREAD";

/** Every type a stored comment can hang off. */
export const CommentableTypeSchema = z.enum([
  ...COMMENTABLE_ENTITY_TYPES,
  FEEDBACK_COMMENTABLE_TYPE,
  CHANGE_SET_COMMENT_THREAD_COMMENTABLE_TYPE,
]);

/** What the generic `comments.*` procedures accept. */
export const EntityCommentableTypeSchema = z.enum(COMMENTABLE_ENTITY_TYPES);

/**
 * Where a record's notes live in the app, relative to the frontend origin.
 * Shared by the bell renderer and the mention email so both deep-link the
 * same way.
 */
export function commentableRecordPath(
  commentableType: z.infer<typeof CommentableTypeSchema>,
  commentableId: string,
): string {
  const id = encodeURIComponent(commentableId);
  switch (commentableType) {
    case "TASK":
      return `/tasks/${id}`;
    case "AP_INVOICE":
      return `/accounts-payable/invoices/${id}`;
    // A released order's detail slide-in, which keeps its notes from approval
    // through acknowledgement.
    case "IM_DRAFT_PURCHASE_ORDER":
      return `/procurement/replenishment/orders?type=po&search=draft%3A${id}`;
    case "FEEDBACK":
      return `/feedback-triage?selected=${id}`;
    // The id is a thread's, not the review's: this page finds the thread's
    // review and opens it on the thread's cell.
    case "IM_CHANGE_SET_COMMENT_THREAD":
      return `/item-master/review-threads/${id}`;
  }
}

export const CommentAuthorTypeSchema = z.enum(["user", "agent", "system"]);

/** `shared` = visible to everyone who can see the commentable; `internal` = triage only. */
export const CommentVisibilitySchema = z.enum(["shared", "internal"]);

// --- Attachment schema ---

export const CommentAttachmentSchema = z.object({
  documentId: z.uuid(),
  url: z.string(),
  mimeType: z.string(),
  fileName: z.string(),
});

// --- Response schema ---

export const CommentResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  commentableType: CommentableTypeSchema,
  commentableId: z.uuid(),
  authorType: CommentAuthorTypeSchema,
  authorUserId: z.uuid().nullable(),
  authorAgentDefinitionId: z.uuid().nullable(),
  /** Plain text; `{user:<uuid>}` tokens mark @mentions. */
  body: z.string(),
  attachments: z.array(CommentAttachmentSchema).nullable(),
  /**
   * Users the body mentions who could see the record when it was last
   * written. A token for anyone else stays in the body but is not listed here
   * and triggers no notification.
   */
  mentionedUserIds: z.array(z.uuid()),
  /** Null on rows that predate the column; treat as shared. */
  visibility: CommentVisibilitySchema.nullable().optional(),
  readAt: z.coerce.date().nullable(),
  readByAgentDefinitionId: z.uuid().nullable(),
  /** When the author last edited the body; null or absent = never edited. */
  editedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// --- Input schemas ---

const CommentableRefSchema = z.object({
  commentableType: EntityCommentableTypeSchema,
  commentableId: z.uuid(),
});

export const CreateCommentInputSchema = CommentableRefSchema.extend({
  body: z.string().trim().min(1).max(50000),
  attachments: z.array(CommentAttachmentSchema).optional(),
});

export const ListCommentsInputSchema = CommentableRefSchema.extend({
  limit: z.int().min(1).max(200).prefault(50),
  offset: z.int().min(0).prefault(0),
  /** By `createdAt`. `desc` pages from the newest note back. */
  order: z.enum(["asc", "desc"]).prefault("asc"),
});

export const ListCommentsResponseSchema = z.object({
  comments: z.array(CommentResponseSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

export const UpdateCommentInputSchema = z.object({
  id: z.uuid(),
  body: z.string().trim().min(1).max(50000),
});

export const ListMentionableUsersInputSchema = CommentableRefSchema;

/** Active org users who can read the record, sorted by name. */
export const ListMentionableUsersResponseSchema = z.array(UserBasicInfoSchema);

// --- Contracts ---

export const commentsContracts = {
  create: oc
    .input(CreateCommentInputSchema)
    .output(CommentResponseSchema)
    .route({
      method: "POST",
      summary: "Add a note to a record",
      description:
        "Posts a note (comment) on a task or AP invoice, with optional attachments. Users @mentioned in the body who can see the record are notified.",
    }),

  list: oc
    .input(ListCommentsInputSchema)
    .output(ListCommentsResponseSchema)
    .route({
      method: "GET",
      summary: "List notes on a record",
      description:
        "Returns the paginated notes (comments) on a task or AP invoice, oldest first by default, including author, mentions and attachments.",
    }),

  update: oc
    .input(UpdateCommentInputSchema)
    .output(CommentResponseSchema)
    .route({
      method: "PUT",
      summary: "Edit your note",
      description:
        "Changes the text of a note you wrote. Only newly @mentioned users are notified.",
    }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "DELETE",
      summary: "Delete your note",
      description:
        "Permanently deletes a note (comment) that you wrote on a task or AP invoice.",
    }),

  listMentionableUsers: oc
    .input(ListMentionableUsersInputSchema)
    .output(ListMentionableUsersResponseSchema)
    .route({
      method: "GET",
      summary: "List users you can mention on a record",
      description:
        "Returns active users in your organization who can read the given task or AP invoice, sorted by name, for @mentions in notes.",
    }),
} as const;

// --- Type exports ---

export type CommentableType = z.infer<typeof CommentableTypeSchema>;
export type EntityCommentableType = z.infer<typeof EntityCommentableTypeSchema>;
export type CommentAuthorType = z.infer<typeof CommentAuthorTypeSchema>;
export type CommentVisibility = z.infer<typeof CommentVisibilitySchema>;
export type CommentAttachment = z.infer<typeof CommentAttachmentSchema>;
export type CommentResponse = z.infer<typeof CommentResponseSchema>;
export type CreateCommentInput = z.infer<typeof CreateCommentInputSchema>;
export type ListCommentsInput = z.infer<typeof ListCommentsInputSchema>;
export type ListCommentsResponse = z.infer<typeof ListCommentsResponseSchema>;
export type UpdateCommentInput = z.infer<typeof UpdateCommentInputSchema>;
export type ListMentionableUsersInput = z.infer<
  typeof ListMentionableUsersInputSchema
>;
export type ListMentionableUsersResponse = z.infer<
  typeof ListMentionableUsersResponseSchema
>;
